/**
 * La grille tarifaire (docs/10 §8).
 *
 * **Ouverte au visiteur anonyme** : exiger un compte pour comparer les offres
 * ferait renoncer avant l'inscription. `is_current` est alors faux partout, ce
 * qui est exact.
 *
 * Rien n'est calculé ici. Les noms, descriptions, libellés de capacités et
 * valeurs viennent du serveur, dans la langue de l'appelant : un administrateur
 * qui ajoute une offre doit pouvoir la nommer sans livrer une version du client
 * (docs/22 §4). Les seules chaînes traduites côté frontend sont celles de
 * l'écran lui-même — le titre, les intervalles de facturation, le mot
 * « illimité ».
 */
import { useTranslation } from "react-i18next";

import { usePlans, type FeatureValue, type Plan } from "@/features/billing/api/useBilling";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function PlansPage() {
  const { t } = useTranslation("billing");
  const plans = usePlans();

  if (plans.isPending) {
    return <Skeleton className="mx-auto my-8 h-96 w-full max-w-4xl" label={t("plans.title")} />;
  }
  if (plans.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("plans.title")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("plans.subtitle")}</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        {plans.data.map((plan) => (
          <PlanCard key={plan.id} plan={plan} />
        ))}
      </div>
    </div>
  );
}

function PlanCard({ plan }: { plan: Plan }) {
  const { t } = useTranslation("billing");
  // Le premier tarif fait office de vitrine. Le serveur les trie par montant
  // croissant, donc c'est le mensuel qui s'affiche : annoncer « 120 €/an » sur
  // une carte de comparaison ferait paraître l'offre dix fois plus chère que
  // l'offre gratuite qu'elle jouxte.
  const [price] = plan.prices;

  return (
    <Card title={plan.name}>
      {plan.is_current ? (
        <p className="text-brand-600 dark:text-brand-400 text-sm font-medium">
          {t("plans.current")}
        </p>
      ) : null}
      {plan.description !== null ? (
        <p className="text-sm text-slate-600 dark:text-slate-400">{plan.description}</p>
      ) : null}

      <p className="mt-3 text-2xl font-semibold">
        {price === undefined ? (
          t("plans.free")
        ) : (
          <Price amountCents={price.amount_cents} interval={price.interval} />
        )}
      </p>

      <ul className="mt-4 space-y-1 text-sm">
        {plan.features.map((feature) => (
          <li key={feature.code} className="flex justify-between gap-4">
            <span className="text-slate-600 dark:text-slate-400">{feature.label}</span>
            <FeatureValueText feature={feature} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Le montant, formaté depuis des **centimes entiers**.
 *
 * La division par 100 a lieu ici, au dernier moment et pour l'affichage seul :
 * aucun calcul ne repart de ce nombre. Faire voyager un décimal depuis le
 * serveur le ferait passer par un flottant JSON, et `12,00 €` reviendrait
 * `11.999999999999998`.
 */
function Price({ amountCents, interval }: { amountCents: number; interval: string }) {
  const { t, i18n } = useTranslation("billing");
  const formatted = new Intl.NumberFormat(i18n.language, {
    style: "currency",
    currency: "EUR",
  }).format(amountCents / 100);
  return (
    <>
      {formatted}
      <span className="text-base font-normal text-slate-500">
        {" "}
        {t([`plans.interval.${interval}`, "plans.interval.month"])}
      </span>
    </>
  );
}

/**
 * La valeur d'une capacité, lue **avec son type**.
 *
 * `value === null` signifie « illimité » et `0` signifie « rien ». Les deux sont
 * faux en JavaScript, et un `{feature.value || t("unlimited")}` — la façon
 * naturelle de l'écrire — afficherait « illimité » sur le quota nul du plan
 * gratuit. C'est pour rendre cette confusion impossible que le serveur envoie
 * `type` à côté de `value`.
 */
function FeatureValueText({ feature }: { feature: FeatureValue }) {
  const { t } = useTranslation("billing");

  if (feature.type === "boolean") {
    return (
      <span
        className={feature.value === true ? "font-medium" : "text-slate-500 dark:text-slate-400"}
      >
        {feature.value === true ? t("plans.included") : t("plans.excluded")}
      </span>
    );
  }
  if (feature.value === null) {
    return <span className="font-medium">{t("plans.unlimited")}</span>;
  }
  return (
    <span className="font-medium">
      {String(feature.value)}
      {feature.unit !== null ? ` ${t([`plans.unit.${feature.unit}`, "plans.unit.count"])}` : ""}
    </span>
  );
}
